import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { listClients } from "../api/clients";
import { formatBusinessType, formatIndustry, type Client } from "../types/client";
import { useAuth } from "../hooks/useAuth";
import { CreateClientForm } from "../components/CreateClientForm";
import { ErrorMessage } from "../components/ErrorMessage";
import { ThemeSelector } from "../components/ThemeSelector";
import { PageTransition } from "../components/ui/PageTransition";
import { AnimatedCard } from "../components/ui/AnimatedCard";

// The business type the create form sends for personal books (requirements 5d).
const PERSONAL = "personal";

function greetingForTime(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

interface ClientCardProps {
  client: Client;
  index: number;
  onOpen: () => void;
}

/**
 * One card of the list. Personal books carry the label "Personal books" in
 * place of an industry and an entity type (walk-through round 1, finding 8).
 */
function ClientCard({ client, index, onOpen }: ClientCardProps): ReactNode {
  const personal = client.business_type === PERSONAL;
  return (
    <AnimatedCard as="li" style={{ transitionDelay: `${index * 50}ms` }}>
      <button type="button" className="client-card" onClick={onOpen}>
        <div className="client-card-content">
          <div className="client-card-name">{client.business_name}</div>
          <div className="client-card-meta">
            <span className="industry-badge">
              {personal ? "Personal books" : formatIndustry(client.industry)}
            </span>
            {!personal && (
              <span className="entity-type">{formatBusinessType(client.business_type)}</span>
            )}
            {client.created_at && (
              <span className="client-card-date">Created {formatDate(client.created_at)}</span>
            )}
          </div>
        </div>
        <span className="client-card-arrow" aria-hidden="true">
          &#8250;
        </span>
      </button>
    </AnimatedCard>
  );
}

export function ClientListPage(): ReactNode {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // The "Add personal books" door (5d) arrives with ?add=personal: the form
  // opens with personal books chosen (walk-through round 1, finding 7).
  const [searchParams] = useSearchParams();
  const startPersonal = searchParams.get("add") === PERSONAL;
  const [showForm, setShowForm] = useState(startPersonal);
  const initialType = startPersonal ? PERSONAL : undefined;

  useEffect(() => {
    async function load(): Promise<void> {
      try {
        const data = await listClients();
        setClients(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load businesses");
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  function handleClientCreated(client: Client): void {
    setClients((prev) => [...prev, client]);
    setShowForm(false);
  }

  const firstName = user?.display_name?.split(" ")[0] ?? "there";
  // "You have N businesses" counts businesses only; personal books are listed
  // under their own heading (walk-through round 1, finding 8).
  const businesses = clients.filter((c) => c.business_type !== PERSONAL);
  const personalBooks = clients.filter((c) => c.business_type === PERSONAL);
  const openClient = (id: number) => () => navigate(`/clients/${id}`);

  return (
    <main className="client-list-page">
      <header>
        <h1>prime</h1>
        <div className="user-info">
          <ThemeSelector />
          <span>{user?.display_name}</span>
          <button type="button" onClick={() => void logout()}>
            Log out
          </button>
        </div>
      </header>

      <PageTransition>
        <AnimatePresence>
          {error && <ErrorMessage message={error} onDismiss={() => setError("")} />}
        </AnimatePresence>

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            Loading your businesses...
          </div>
        ) : clients.length === 0 && !showForm ? (
          <div className="empty-state">
            <div className="empty-state-icon">&#128203;</div>
            <h2>Welcome to prime, {firstName}</h2>
            <p>Create your first business to get started with AI-powered bookkeeping.</p>
            <CreateClientForm onCreated={handleClientCreated} initialType={initialType} />
          </div>
        ) : (
          <>
            <div className="welcome-section">
              <h2>
                {greetingForTime()}, {firstName}
              </h2>
              <p className="welcome-subtitle">
                You have {businesses.length} {businesses.length === 1 ? "business" : "businesses"}
              </p>
            </div>

            <div className="section-header">
              <h2>Your businesses</h2>
            </div>

            <ul className="client-cards">
              {businesses.map((client, index) => (
                <ClientCard
                  key={client.id}
                  client={client}
                  index={index}
                  onOpen={openClient(client.id)}
                />
              ))}
              <li>
                <button
                  type="button"
                  className="add-business-card"
                  onClick={() => setShowForm(true)}
                >
                  <span className="add-business-icon">+</span>
                  Add a business
                </button>
              </li>
            </ul>

            {personalBooks.length > 0 && (
              <>
                <div className="section-header">
                  <h2>Personal books</h2>
                </div>
                <ul className="client-cards">
                  {personalBooks.map((client, index) => (
                    <ClientCard
                      key={client.id}
                      client={client}
                      index={index}
                      onOpen={openClient(client.id)}
                    />
                  ))}
                </ul>
              </>
            )}

            {showForm && (
              <CreateClientForm onCreated={handleClientCreated} initialType={initialType} />
            )}
          </>
        )}
      </PageTransition>
    </main>
  );
}
